import React, { useState } from 'react';
import { Accordion, accordionItemBuilder, Box, Text } from '@wix/design-system';
import { ChevronDown, ChevronRight } from '@wix/wix-ui-icons-common/lazy';
import css from './app-permissions-accordion.scss';
import { useEssentials, useTranslation } from '@wix/app-market-core';
import type { Permission } from '@wix/app-market-services';
import { AppPermissionsDataHooks } from './data-hooks';
import type { AppPageType } from '@wix/app-market-core-components-types';
import { appMarketAppPageClick } from '@wix/bi-logger-app-market-data/v2';

interface AppPermissionsAccordionProps {
  appId: string;
  appName: string;
  permissions: Permission[];
  appPageType: AppPageType;
}

export function AppPermissionsAccordion({
  appName,
  appId,
  permissions,
  appPageType,
}: AppPermissionsAccordionProps) {
  const { t } = useTranslation();
  const { biLogger } = useEssentials();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const permissionsAccordionItems = permissions.map((permission, index) =>
    accordionItemBuilder({
      buttonType: 'node',
      showLabel: 'always',
      title: (
        <Text size="small" weight="thin">
          {permission.title}
        </Text>
      ),
      expandLabel: <ChevronRight />,
      collapseLabel: <ChevronDown />,
      children: (
        <Text size="small" secondary>
          {permission.description}
        </Text>
      ),
      open: openIndex === index,
      onToggle: () => {
        const isToggleExpanded = openIndex !== index;
        setOpenIndex(isToggleExpanded ? index : null);
        biLogger.report(
          appMarketAppPageClick({
            app_id: appId,
            cta: isToggleExpanded
              ? 'expand_permissions'
              : 'collapse_permissions',
            location: 'slim_app_page',
            app_page_type: appPageType,
            value: index.toString(),
          }),
        );
      },
    }),
  );

  if (permissions.length === 0) {
    return null;
  }

  return (
    <Box
      gap="6px"
      direction="vertical"
      dataHook={AppPermissionsDataHooks.CONTAINER}
    >
      <Text size="medium" weight="bold">
        {t('app.page.permissions.title', {
          name: appName,
          interpolation: { escapeValue: false },
        })}
      </Text>

      <Box direction="vertical" className={css.permissionItem}>
        <Accordion
          hideShadow
          skin="light"
          size="tiny"
          horizontalPadding="none"
          items={permissionsAccordionItems}
        />
      </Box>
    </Box>
  );
}
